import { Tooltip } from "@material-ui/core";
import { WarningRounded } from "@material-ui/icons";
import React, { useMemo } from "react";

import { Event, EventForDisplay } from "../../../dtos/event";
import { getEventNameDescription } from "../../../utils/getEventNameDescription";
import { isEventUserDeleted } from "../../../utils/isEventUserDeleted";
import { getTimestamp } from "../../../utils/getTimestamp";
import { ResponsiveDataView, Column } from "../../shared/ResponsiveDataView";
import { ViewEmployeeLink } from "../../shared/ViewEmployee";

interface Props {
    events: Event[];
    filterByLocalTime: boolean | null;
    isLoading?: boolean;
}

export function ReportLogTable({
    events,
    filterByLocalTime,
    isLoading,
}: Props) {

    const reportLogsForDisplay: EventForDisplay[] = useMemo(
        () =>
            events.map((e) => {
                return {
                    accountType: e.accountType,
                    description: e.description,
                    eventName: e.eventNameForDisplay || getEventNameDescription(e.eventType, e.eventName),
                    timestamp: getTimestamp(filterByLocalTime, e.localTimestamp, e.timestamp),
                    user: e.employeeName || e.customerName,
                    userId: e.employeeId || e.customerId,
                    id: e.id,
                    isUserEmployee: Boolean(e.employeeId),
                    isUserDeleted: isEventUserDeleted(e),
                    uniqueSiteId: e.uniqueSiteId,
                    siteName: e.siteName,
                    organizationName: e.organizationName,
                    wasInMaintenanceMode: Boolean(e.wasInMaintenanceMode),
                };
            }),
        [events, filterByLocalTime]
    );

    const maintenanceColor = (e: EventForDisplay) => (e.wasInMaintenanceMode ? "#f57c00" : undefined);

    const columns: Column<EventForDisplay>[] = [
        {
            key: 'uniqueSiteId',
            label: 'Site Id',
            primary: true,
            render: (e) => (
                <Tooltip title={`${e.organizationName ? e.organizationName + " - " : ""}${e.siteName}`}>
                    <span>{e.uniqueSiteId}</span>
                </Tooltip>
            ),
        },
        { key: 'user', label: 'User', hideWhenEmpty: true },
        {
            key: 'userId',
            label: 'User Id',
            hideWhenEmpty: true,
            render: (e) => (e.isUserEmployee
                ? <ViewEmployeeLink userId={e.userId} isUserDeleted={e.isUserDeleted} />
                : <span>{e.userId}</span>),
        },
        { key: 'accountType', label: 'Account Type', hideWhenEmpty: true },
        {
            key: 'timestamp',
            label: 'Timestamp',
            render: (e) => <span style={{ color: maintenanceColor(e) }}>{e.timestamp}</span>,
        },
        {
            key: 'eventName',
            label: 'Event',
            render: (e) => (
                <span style={{ color: maintenanceColor(e) }}>
                    {e.wasInMaintenanceMode && (
                        <Tooltip title="This event occurred during maintenance mode">
                            <WarningRounded style={{ fontSize: "1rem", verticalAlign: "middle", marginRight: "4px" }} />
                        </Tooltip>
                    )}
                    {e.eventName}
                </span>
            ),
        },
        {
            key: 'description',
            label: 'Description',
            render: (e) => <span style={{ color: maintenanceColor(e) }}>{e.description}</span>,
        },
    ];

    return (
        <ResponsiveDataView
            rows={reportLogsForDisplay}
            columns={columns}
            rowKey={(e) => `${e.id}_${e.eventName}`}
            loading={isLoading}
        />
    );
}
